<template>
  <div class="min-h-screen flex flex-col">
    <!-- Header -->
    <header
      ref="headerRef"
      class="fixed left-0 w-full z-50 backdrop-blur-md bg-hk-cream/90 shadow-sm"
      :style="{ top: navVisible ? '0px' : `-${headerHeight || 200}px`, transition: 'top 0.3s ease' }"
    >
      <div class="container mx-auto px-4 pt-3 pb-2 md:pt-6 md:pb-2">
        <!-- Top bar: Logo + Language -->
        <div class="flex items-center justify-between mb-1 md:mb-4">
          <router-link to="/" class="flex flex-col group">
            <span class="text-2xl md:text-3xl font-extrabold tracking-tight">
              <span class="text-hk-gray transition-colors group-hover:text-hk-dark">HAS</span><span class="text-hk-red">KREDIET</span>
            </span>
            <span class="text-xs italic text-hk-red tracking-wide">SINCE 1995</span>
          </router-link>
          <div class="hidden sm:flex flex-col items-end gap-1">
            <span class="text-xs text-hk-red font-medium">{{ t('global.loanCost') }}</span>
            <div class="flex items-center gap-1 text-sm font-semibold text-hk-dark">
              <button
                v-for="(lang, i) in locales"
                :key="lang"
                :class="locale === lang ? 'text-hk-red' : 'text-hk-gray hover:text-hk-dark'"
                :data-umami-event="`language-${lang}`"
                @click="setLocale(lang)"
              >
                <span v-if="i > 0" class="text-hk-gray mr-1">/</span>{{ lang.toUpperCase() }}
              </button>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="hidden md:flex items-center gap-8">
          <router-link
            v-for="item in navigation"
            :key="item.key"
            :to="item.href"
            :class="[
              'text-sm font-bold uppercase tracking-wide transition-colors',
              $route.path === item.href
                ? 'text-hk-red'
                : 'text-hk-dark hover:text-hk-red',
            ]"
          >
            {{ t(item.key) }}
          </router-link>
          <div class="ml-auto flex items-center gap-3">
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Sleepstraat+186A,+9000+Gent,+Belgium"
              target="_blank"
              rel="noopener noreferrer"
              data-umami-event="route-click"
              class="flex items-center gap-1.5 bg-hk-red text-white font-bold uppercase text-sm px-5 py-2.5 rounded-full hover:bg-hk-red-dark transition-colors tracking-wide"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ t('footer.maps') }}
            </a>
            <router-link
              to="/contact"
              class="bg-hk-red text-white font-bold uppercase text-sm px-6 py-2.5 rounded-full hover:bg-hk-red-dark transition-colors tracking-wide"
            >
              {{ t('nav.contact') }}
            </router-link>
          </div>
        </nav>

        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center justify-between">
          <button
            class="text-hk-dark p-1 -ml-1"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div class="flex flex-col items-end gap-0.5 sm:hidden min-w-0 pl-3">
            <span class="text-xs text-hk-red font-medium text-right leading-tight">{{ t('global.loanCost') }}</span>
            <div class="flex items-center gap-1 text-sm font-semibold">
              <button
                v-for="(lang, i) in locales"
                :key="lang"
                :class="locale === lang ? 'text-hk-red' : 'text-hk-gray'"
                :data-umami-event="`language-${lang}`"
                @click="setLocale(lang)"
              >
                <span v-if="i > 0" class="text-hk-gray mr-1">/</span>{{ lang.toUpperCase() }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <hr class="border-hk-cream-dark" />
    </header>

    <!-- Mobile drawer overlay -->
    <Transition name="mobile-fade">
      <div v-if="mobileMenuOpen" class="fixed inset-0 z-40 bg-black/40" @click="mobileMenuOpen = false"></div>
    </Transition>
    <Transition name="mobile-slide">
      <div v-if="mobileMenuOpen" class="fixed top-0 left-0 z-50 h-full w-72 bg-hk-cream shadow-2xl flex flex-col">
        <div class="flex items-center justify-between px-5 pt-6 pb-4 border-b border-hk-cream-dark">
          <span class="text-xl font-extrabold tracking-tight">
            <span class="text-hk-gray">HAS</span><span class="text-hk-red">KREDIET</span>
          </span>
          <button class="text-hk-dark p-1" @click="mobileMenuOpen = false">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav class="flex-1 overflow-y-auto px-5 py-6 space-y-1">
          <router-link
            v-for="item in navigation"
            :key="item.key"
            :to="item.href"
            :class="[
              'block px-3 py-2.5 rounded-xl text-sm font-bold uppercase tracking-wide transition-colors',
              $route.path === item.href
                ? 'bg-hk-red/10 text-hk-red'
                : 'text-hk-dark hover:bg-hk-cream-dark hover:text-hk-red',
            ]"
            @click="mobileMenuOpen = false"
          >
            {{ t(item.key) }}
          </router-link>
        </nav>

        <div class="px-5 pb-6 space-y-3 border-t border-hk-cream-dark pt-4">
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Sleepstraat+186A,+9000+Gent,+Belgium"
            target="_blank"
            rel="noopener noreferrer"
            data-umami-event="route-click-mobile"
            class="flex items-center justify-center gap-2 w-full bg-hk-red text-white font-bold uppercase text-sm px-5 py-2.5 rounded-full hover:bg-hk-red-dark transition-colors tracking-wide"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {{ t('footer.maps') }}
          </a>
          <router-link
            to="/contact"
            class="block text-center w-full bg-hk-red text-white font-bold uppercase text-sm px-5 py-2.5 rounded-full hover:bg-hk-red-dark transition-colors tracking-wide"
            @click="mobileMenuOpen = false"
          >
            {{ t('nav.contact') }}
          </router-link>
        </div>
      </div>
    </Transition>

    <!-- Spacer for fixed header -->
    <div ref="headerSpacer" :style="{ height: headerHeight + 'px' }"></div>

    <!-- Main content -->
    <main class="flex-1 relative">
      <router-view />
      <!-- Wave bottom — adapts to page background -->
      <div class="wave-divider bottom-0">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" class="w-full h-8 md:h-12">
          <path d="M0,0 C360,60 720,20 1080,50 C1260,60 1380,40 1440,30 L1440,60 L0,60 Z" fill="#2D2D2D"/>
        </svg>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-hk-dark text-white section-decorated -mt-px">

      <div class="container mx-auto px-4 pt-16 pb-10 relative z-10">
        <div class="flex flex-col md:flex-row items-center md:items-start justify-between gap-10">
          <div class="text-center md:text-left">
            <div class="text-2xl font-extrabold tracking-tight mb-5">
              <span class="text-white/70">HAS</span><span class="text-hk-red-light">KREDIET</span>
            </div>
            <nav class="flex flex-col items-center md:items-start gap-2">
              <router-link
                v-for="link in footerLinks"
                :key="link.key"
                :to="link.href"
                class="text-sm font-medium text-white/70 hover:text-white tracking-wide transition-colors"
              >
                {{ t(link.key) }}
              </router-link>
            </nav>
          </div>

          <div class="text-center md:text-right">
            <div class="text-sm text-white/80 leading-relaxed space-y-1">
              <p class="font-semibold text-white">HASKREDIET BV</p>
              <p>Sleepstraat 186 A - 9000 Gent</p>
              <p>Tel. 09/267.30.30</p>
              <p>E-mail: <a href="mailto:info@haskrediet.be" class="text-hk-red-light hover:text-white transition-colors">info@haskrediet.be</a></p>
              <p class="pt-2">{{ t('footer.hours') }}</p>
              <p>{{ t('footer.saturday') }}</p>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Sleepstraat+186A,+9000+Gent,+Belgium"
              target="_blank"
              rel="noopener noreferrer"
              data-umami-event="route-click-footer"
              class="inline-flex items-center gap-2 mt-5 bg-hk-red text-white btn-modern"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ t('footer.maps') }}
            </a>
          </div>
        </div>
      </div>

      <!-- Bottom bar -->
      <div class="border-t border-white/10 relative z-10">
        <div class="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
          <span>{{ t('global.loanCost') }}</span>
          <span>&copy; 2026 {{ t('footer.rights') }}</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "./i18n";
import { useLocaleStore } from "./stores/locale-store";
import type { Locale } from "./stores/locale-store";

const localeStore = useLocaleStore();
const { t, locale } = useI18n();
const mobileMenuOpen = ref(false);
const headerRef = ref<HTMLElement | null>(null);

watch(mobileMenuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
});

const navVisible = ref(true);
let lastScrollY = 0;
const headerHeight = ref(0);

const onScroll = (e: Event) => {
  // On mobile (< md breakpoint) the navbar always stays visible
  if (window.innerWidth < 768) {
    navVisible.value = true;
    return;
  }
  let currentY = document.scrollingElement?.scrollTop ?? window.scrollY;
  const target = e.target as EventTarget | null;
  if (target && target !== document && (target as HTMLElement).scrollTop !== undefined) {
    currentY = Math.max(currentY, (target as HTMLElement).scrollTop);
  }
  if (currentY > lastScrollY && currentY > 50) {
    navVisible.value = false;
  } else if (currentY < lastScrollY) {
    navVisible.value = true;
  }
  lastScrollY = currentY;
};

// Tracks header size changes from late-loading web fonts, locale switches and rotation
let headerResizeObserver: ResizeObserver | null = null;

onMounted(() => {
  // capture: true catches scroll events from any scrolling element, not just window
  window.addEventListener("scroll", onScroll, { passive: true, capture: true });
  if (headerRef.value) {
    headerResizeObserver = new ResizeObserver(() => {
      if (headerRef.value) headerHeight.value = headerRef.value.offsetHeight;
    });
    headerResizeObserver.observe(headerRef.value);
  }
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll, { capture: true });
  headerResizeObserver?.disconnect();
});

const locales: Locale[] = ["nl", "fr", "en", "tr"];

const setLocale = (l: Locale) => {
  localeStore.setLocale(l);
};

const navigation = [
  { key: "nav.home", href: "/" },
  { key: "nav.woonlening", href: "/woonlening" },
  { key: "nav.loa", href: "/leningen-op-afbetaling"},
  { key: "nav.leasing", href: "/leasing" },
  { key: "nav.ondernemers", href: "/ondernemers" },
  { key: "nav.verzekeringen", href: "/verzekeringen" },
  { key: "nav.wieZijnWij", href: "/wie-zijn-wij" },
];

const footerLinks = [
  { key: "nav.home", href: "/" },
  { key: "nav.wieZijnWij", href: "/wie-zijn-wij" },
  { key: "footer.simuleer", href: "/#simuleer" },
  { key: "footer.juridisch", href: "/juridische-mededeling" },
  { key: "footer.privacy", href: "/privacy" },
  { key: "nav.contact", href: "/contact" },
];
</script>
